import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setFiles } from './filesSlice';

export enum MediaType {
  IMAGE = "IMAGE",
  VIDEO = "VIDEO",
}

export interface CreateThumbnail {
  name: string;
  mediaType: MediaType | undefined;
}

export interface UpdateThumbnail {
  name: string;
  thumbnailUrl: string | null;
}

export interface MediaThumbnail {
  name: string;
  thumbnailUrl: string | undefined | null;
  mediaType: MediaType | undefined;
}

interface ThumbnailState {
  thumbnails: MediaThumbnail[];
  selectedFileNames: string[];
}

const initialState: ThumbnailState = {
  thumbnails: [],
  selectedFileNames: []
};

export const thumbnailSlice = createSlice({
  name: 'thumbnails',
  initialState,
  reducers: {
    createThumbnail: (state, action: PayloadAction<CreateThumbnail>) => {
      const exists = state.thumbnails.some(t => t.name === action.payload.name);
      if (!exists) {
        const thumbnail: MediaThumbnail = {
          name: action.payload.name,
          thumbnailUrl: undefined,
          mediaType: action.payload.mediaType
        }
        state.thumbnails = [...state.thumbnails, thumbnail]
      }
    },
    updateThumbnail: (state, action: PayloadAction<UpdateThumbnail>) => {
      const mediaThumbnail = state.thumbnails
        .find(t => t.name === action.payload.name);

      if (mediaThumbnail) {
        mediaThumbnail.thumbnailUrl = action.payload.thumbnailUrl;
      }
    },
    selectMedia: (state, action: PayloadAction<string>) => {
      if (!state.selectedFileNames.includes(action.payload)) {
        state.selectedFileNames.push(action.payload);
      }
    },
    
    deselectMedia: (state, action: PayloadAction<string>) => {
      state.selectedFileNames = state.selectedFileNames.filter(
        name => name !== action.payload
      );
    },
    deleteSelected: (state, action: PayloadAction<string[]>) => {
      const selected = new Set(action.payload);
      state.thumbnails.forEach(thumbnail => {
        if (selected.has(thumbnail.name) && thumbnail.thumbnailUrl) {
          URL.revokeObjectURL(thumbnail.thumbnailUrl);
        }
      });

      state.thumbnails = state.thumbnails.filter(
        t => !selected.has(t.name)
      );

      state.selectedFileNames = [];
    },
    clearSelection: (state) => {
      state.selectedFileNames = [];
    },
    clearThumbnails: (state) => {
      state.thumbnails.forEach(thumbnail => {
        if (thumbnail.thumbnailUrl) {
          URL.revokeObjectURL(thumbnail.thumbnailUrl);
        }
      });
      
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setFiles, (state, action) => {
      const fileNames = new Set(action.payload.map(f => f.name));
      state.thumbnails.forEach(thumbnail => {
        if (!fileNames.has(thumbnail.name) && thumbnail.thumbnailUrl) {
          URL.revokeObjectURL(thumbnail.thumbnailUrl);
        }
      });
      state.thumbnails = state.thumbnails.filter(t => fileNames.has(t.name));
      state.selectedFileNames = state.selectedFileNames.filter(name => fileNames.has(name));
    });
  },
});

export const {
  createThumbnail,
  updateThumbnail,
  selectMedia,
  deselectMedia,
  deleteSelected,
  clearSelection,
  clearThumbnails
} = thumbnailSlice.actions;

export default thumbnailSlice.reducer;
